const baseURL = process.env.REACT_APP_BASE_URL || 'https://team.tomi.la/server/';

/**
 * Función helper para llamar a endpoints PHP del backend de pines
 * Similar a loadJson pero para endpoints GET con query params
 */
async function loadPinsJson(url, queryParams = {}) {
  try {
    // Construir URL con query params
    const urlObj = new URL(baseURL + url + '.php', window.location.origin);
    Object.keys(queryParams).forEach(key => {
      if (queryParams[key] !== undefined && queryParams[key] !== null) {
        urlObj.searchParams.append(key, queryParams[key]);
      }
    });

    const response = await fetch(urlObj.toString(), {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      }
    });

    const resp = await response.text();

    if (resp === "INVALID") {
      return "INVALID";
    }

    // Si hay un error HTTP, intentar parsear el JSON del error
    if (!response.ok) {
      try {
        const errorData = JSON.parse(resp);
        console.error('API Error:', errorData);
        // Si el error tiene información detallada, mostrarla
        if (errorData.error && errorData.message) {
          console.error('Error message:', errorData.message);
          console.error('Error file:', errorData.file);
          console.error('Error line:', errorData.line);
          if (errorData.trace) {
            console.error('Error trace:', errorData.trace);
          }
        }
        if (errorData?.error === 'PinCreateLotServiceError'
          || errorData?.error === 'PinStatusServiceError'
          || errorData?.error === 'PinBatchStatusServiceError') {
          return errorData;
        }
        return "INVALID";
      } catch (err) {
        console.error('Error response (not JSON):', resp);
        return "INVALID";
      }
    }

    try {
      if (resp === "[]") {
        return null;
      }
      const jsonResult = JSON.parse(resp);
      return jsonResult;
    } catch (err) {
      console.error('JSON parse error:', err, 'Response:', resp);
      return resp;
    }
  } catch (error) {
    console.error('Error:', error);
    return null;
  }
}

/**
 * Helper para POST a endpoints PHP del backend de pines
 */
async function postPinsJson(url, formData) {
  try {
    const response = await fetch(baseURL + url + '.php', {
      method: 'POST',
      body: formData
    });

    const resp = await response.text();

    if (resp === "INVALID") {
      return "INVALID";
    }

    if (!response.ok) {
      try {
        const errorData = JSON.parse(resp);
        console.error('API Error:', errorData);
        if (errorData?.error === 'PinCreateLotServiceError'
          || errorData?.error === 'PinStatusServiceError'
          || errorData?.error === 'PinBatchStatusServiceError'
          || errorData?.error === 'PinAssignServiceError'
          || errorData?.error === 'MissingParameters'
          || errorData?.error === 'InvalidEmail') {
          return errorData;
        }
        return "INVALID";
      } catch (err) {
        console.error('Error response (not JSON):', resp);
        return "INVALID";
      }
    }

    try {
      if (resp === "[]") {
        return null;
      }
      const jsonResult = JSON.parse(resp);
      return jsonResult;
    } catch (err) {
      console.error('JSON parse error:', err, 'Response:', resp);
      return resp;
    }
  } catch (error) {
    console.error('Error:', error);
    return null;
  }
}

/**
 * POST multipart (CSV/TXT) sin fijar Content-Type manualmente.
 * @param {string} urlPathWithoutPhp ej. pins_api/PinBatchStatusService
 * @param {FormData} formData debe incluir token, csv_kind, file
 */
async function postPinsMultipart(urlPathWithoutPhp, formData) {
  try {
    const response = await fetch(baseURL + urlPathWithoutPhp + '.php', {
      method: 'POST',
      body: formData
    });

    const resp = await response.text();

    if (resp === 'INVALID') {
      return 'INVALID';
    }

    if (!response.ok) {
      try {
        const errorData = JSON.parse(resp);
        console.error('API Error:', errorData);
        if (errorData?.error === 'PinBatchStatusServiceError'
          || errorData?.error === 'MissingParameters'
          || errorData?.error === 'MissingFile'
          || errorData?.error === 'InvalidFile') {
          return errorData;
        }
        return 'INVALID';
      } catch (err) {
        console.error('Error response (not JSON):', resp);
        return 'INVALID';
      }
    }

    try {
      if (resp === '[]') {
        return null;
      }
      return JSON.parse(resp);
    } catch (err) {
      console.error('JSON parse error:', err, 'Response:', resp);
      return resp;
    }
  } catch (error) {
    console.error('Error:', error);
    return null;
  }
}

/**
 * Helper para descargar archivos desde endpoints PHP del backend de pines
 */
async function downloadPinsFile(url, formData) {
  const response = await fetch(baseURL + url + '.php', {
    method: 'POST',
    body: formData
  });

  const contentType = response.headers.get('content-type') || '';
  if (!response.ok || contentType.includes('application/json') || contentType.includes('text/plain')) {
    const respText = await response.text();
    if (respText === 'INVALID') {
      return 'INVALID';
    }
    return null;
  }

  const blob = await response.blob();
  const disposition = response.headers.get('content-disposition') || '';
  const match = /filename=([^;]+)/i.exec(disposition);
  const filename = match ? match[1].trim().replace(/(^"|"$)/g, '') : 'export.csv';
  return { blob, filename };
}

/**
 * Servicio de Lotes
 * Usa el backend PHP en server/pins_api que maneja la autenticación JWT automáticamente
 */
export const lotService = {
  /**
   * Listar todos los lotes (paginado)
   * @param {number} page - Número de página (0-indexed)
   * @returns {Promise<Object>} Datos de lotes paginados
   */
  getAll: async (page = 0, token) => {
    return await loadPinsJson('pins_api/LotService', { page, token });
  },

  /**
   * Buscar lote por identificador
   * @param {string} identify - Identificador del lote
   * @returns {Promise<Object>} Datos del lote
   */
  getByIdentify: async (identify, token) => {
    return await loadPinsJson('pins/get_lot_identify', { identify, token });
  },

  /**
   * Obtener PINES de un lote (paginado)
   * @param {number} lotId - ID del lote
   * @param {number} page - Número de página (0-indexed)
   * @returns {Promise<Object>} Datos de PINES paginados
   */
  getPins: async (lotId, page = 0, token) => {
    return await loadPinsJson('pins/get_lot_pins', { lot_id: lotId, page, token });
  },

  /**
   * Crear nuevo lote de PINes
   * @param {number} quantity
   * @param {string} message
   * @param {string} token
   */
  create: async (quantity, message, token) => {
    const data = new FormData();
    data.append('token', token);
    data.append('quantity', quantity);
    data.append('message', message);
    return await postPinsJson('pins_api/PinCreateLotService', data);
  },

  /**
   * Asociar lote con reseller/canal
   * @param {string|number} lot
   * @param {number} channelId
   * @param {string} token
   */
  linkToChannel: async (lot, channelId, token) => {
    const data = new FormData();
    data.append('token', token);
    data.append('lot', lot);
    data.append('channel_id', channelId);
    return await postPinsJson('crm/set/link_lot_to_channel', data);
  },

  /**
   * Obtener canales asociados a una lista de lotes
   * @param {Array<string|number>} lots
   * @param {string} token
   */
  getLotChannels: async (lots, token) => {
    const data = new FormData();
    data.append('token', token);
    data.append('lots', JSON.stringify(lots));
    return await postPinsJson('crm/get/get_lot_channel', data);
  },

  /**
   * Exportar lote a CSV
   * @param {string|number} lotId
   * @param {string} token
   */
  exportLot: async (lotId, token) => {
    const data = new FormData();
    data.append('token', token);
    data.append('lot_id', lotId);
    return await downloadPinsFile('pins_api/PinExportLotService', data);
  }
};

/**
 * Servicio de Lotes QA
 */
export const lotServiceQa = {
  getAll: async (page = 0, token) => {
    return await loadPinsJson('pins_api/LotServiceQA', { page, token });
  },
  create: async (quantity, message, token) => {
    const data = new FormData();
    data.append('token', token);
    data.append('quantity', quantity);
    data.append('message', message);
    return await postPinsJson('pins_api/PinCreateLotServiceQA', data);
  },
  linkToChannel: async (lot, channelId, token) => {
    const data = new FormData();
    data.append('token', token);
    data.append('lot', lot);
    data.append('channel_id', channelId);
    return await postPinsJson('crm/set/link_lot_to_channel', data);
  },
  getLotChannels: async (lots, token) => {
    const data = new FormData();
    data.append('token', token);
    data.append('lots', JSON.stringify(lots));
    return await postPinsJson('crm/get/get_lot_channel', data);
  },
  exportLot: async (lotId, token) => {
    const data = new FormData();
    data.append('token', token);
    data.append('lot_id', lotId);
    return await downloadPinsFile('pins_api/PinExportLotServiceQA', data);
  }
};

/**
 * Servicio de Estado de PIN
 */
export const pinStatusService = {
  /**
   * Buscar estado de PIN por criterio
   * @param {string} type - serial | pin | lot | email
   * @param {string} identify - valor a buscar
   * @param {number} page - página (solo para lotes)
   * @param {string} token - token de sesión
   */
  search: async (type, identify, page = 0, token) => {
    return await loadPinsJson('pins_api/PinStatusService', {
      type,
      identify,
      page,
      token
    });
  },

  /**
   * Consulta en lote desde CSV/TXT (un valor por línea).
   * @param {File} file
   * @param {'pin'|'serial'} csvKind pin → mpins/.../pins/batch, serial → mproducts/.../serials/batch
   * @param {string} token
   */
  searchCsvBatch: async (file, csvKind, token) => {
    const formData = new FormData();
    formData.append('token', token);
    formData.append('csv_kind', csvKind);
    formData.append('file', file);
    return await postPinsMultipart('pins_api/PinBatchStatusService', formData);
  }
};

export const pinStatusServiceQa = {
  search: async (type, identify, page = 0, token) => {
    return await loadPinsJson('pins_api/PinStatusServiceQA', {
      type,
      identify,
      page,
      token
    });
  },

  searchCsvBatch: async (file, csvKind, token) => {
    const formData = new FormData();
    formData.append('token', token);
    formData.append('csv_kind', csvKind);
    formData.append('file', file);
    return await postPinsMultipart('pins_api/PinBatchStatusServiceQA', formData);
  }
};

/**
 * Servicio para validar (activar) PINes seleccionados
 */
export const pinValidateService = {
  /**
   * @param {object} data - payload con time_type_id, pin_type_id y serials/lot_id
   * @param {string} token
   */
  activate: async (data, token) => {
    const formData = new FormData();
    formData.append('token', token);
    formData.append('data', JSON.stringify(data));
    return await postPinsJson('pins_api/PinValidateService', formData);
  }
};

export const pinValidateServiceQa = {
  activate: async (data, token) => {
    const formData = new FormData();
    formData.append('token', token);
    formData.append('data', JSON.stringify(data));
    return await postPinsJson('pins_api/PinValidateServiceQA', formData);
  }
};

/**
 * Servicio de metadatos de PIN (vigencia y tipo de producto)
 */
export const pinMetaService = {
  /**
   * Obtener listas de vigencias y tipos de producto
   * @param {string} token - token de sesión
   */
  getMeta: async (token) => {
    return await loadPinsJson('pins_api/PinMetaService', { token });
  }
};

export const pinMetaServiceQa = {
  getMeta: async (token) => {
    return await loadPinsJson('pins_api/PinMetaServiceQA', { token });
  }
};

/**
 * Reasignar propietario de un PIN por email (prod).
 */
export const pinAssignService = {
  /**
   * @param {{ token: string, pin: string, email: string, project_id: number|string }} params
   */
  assign: async ({ token, pin, email, project_id }) => {
    const formData = new FormData();
    formData.append('token', token);
    formData.append('pin', pin);
    formData.append('email', email);
    formData.append('project_id', project_id);
    return await postPinsJson('pins_api/PinAssignService', formData);
  },
};

// ─── Servicio de Cupones ────────────────────────────────────────────────────

function buildFormData(obj) {
  const fd = new FormData();
  Object.entries(obj).forEach(([k, v]) => {
    if (v !== undefined && v !== null) fd.append(k, v);
  });
  return fd;
}

export const couponService = {
  list: (token) =>
    loadPinsJson('pins_api/CouponListService', { token }),

  create: (stripeCoupon, productIds, token) =>
    postPinsJson(
      'pins_api/CouponCreateService',
      buildFormData({ token, stripe_coupon: stripeCoupon, product_ids: JSON.stringify(productIds) })
    ),

  remove: (id, token) =>
    postPinsJson(
      'pins_api/CouponDeleteService',
      buildFormData({ token, id })
    ),

  linkProduct: (couponId, productId, token) =>
    postPinsJson(
      'pins_api/CouponProductLinkService',
      buildFormData({ token, coupon_id: couponId, product_id: productId })
    ),

  unlinkProduct: (couponId, productId, token) =>
    postPinsJson(
      'pins_api/CouponProductUnlinkService',
      buildFormData({ token, coupon_id: couponId, product_id: productId })
    ),

  products: (token) =>
    loadPinsJson('pins_api/CouponProductsListService', { token }),
};

export const couponServiceQa = {
  list: (token) =>
    loadPinsJson('pins_api/CouponListServiceQA', { token }),

  create: (stripeCoupon, productIds, token) =>
    postPinsJson(
      'pins_api/CouponCreateServiceQA',
      buildFormData({ token, stripe_coupon: stripeCoupon, product_ids: JSON.stringify(productIds) })
    ),

  remove: (id, token) =>
    postPinsJson(
      'pins_api/CouponDeleteServiceQA',
      buildFormData({ token, id })
    ),

  linkProduct: (couponId, productId, token) =>
    postPinsJson(
      'pins_api/CouponProductLinkServiceQA',
      buildFormData({ token, coupon_id: couponId, product_id: productId })
    ),

  unlinkProduct: (couponId, productId, token) =>
    postPinsJson(
      'pins_api/CouponProductUnlinkServiceQA',
      buildFormData({ token, coupon_id: couponId, product_id: productId })
    ),

  products: (token) =>
    loadPinsJson('pins_api/CouponProductsListServiceQA', { token }),
};
